import React, { useState } from "react"
import { graphql, Link } from "gatsby"
import Image from "gatsby-image"
import styled, { css } from "styled-components"
import { FormattedMessage } from "react-intl"

import Layout from "src/layout"
import { useLang } from "src/layout/root"
import { COLORS } from "src/layout/constants"
import { ImageColumn, Square, Sheet, Specs, Bottom } from "src/components/styled"
import Spec from "src/components/Spec"
import Download from "src/svg/download.svg"
import Projects from "src/components/Projects"
import { intersec } from "src/helpers/utils"

const buttonCss = css<{ $selected: boolean }>`
  display: inline-block;
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.black : COLORS.light)};
  margin: 0 20px 20px 0;
  padding: 4px;
  width: 60px;
  height: 60px;
`

const MaterialButton = styled.button<{ $selected: boolean }>`
  ${buttonCss}
  border-radius: 30px;
  .gatsby-image-wrapper {
    border-radius: 25px;
    overflow: hidden;
  }
`
const LiningButton = styled(Link)<{ $selected: boolean }>`
  ${buttonCss}
`
const FormatButton = styled.button<{ $selected: boolean }>`
  display: inline-block;
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.black : COLORS.light)};
  width: 30px;
  height: 30px;
  line-height: 30px;
  margin: 0 25px 40px 0;
`
const MoreInfos = styled.div`
  text-align: right;
  margin: 10px 0 20px;
  a {
    text-decoration: underline;
    font-size: 0.9em;
  }
`

interface DataType {
  websites: { nodes: Website[] }
}
interface ContextType {
  reference: string
}

const LiningPage: GatsbyPage<DataType, ContextType> = ({ data, pageContext, location }) => {
  const { getNodes } = useLang()
  const [website] = getNodes<Website>(data.websites)
  const { categories, collections } = website
  const products = categories.flatMap((category) => category.products)
  const materials = collections.flatMap((collection) => collection.materials)
  const product = products.find(({ reference }) => reference === pageContext.reference)
  const material =
    (location.hash && materials.find(({ reference }) => reference === location.hash.slice(1))) || materials[0]
  const [format, setFormat] = useState(product.formats[0])

  const projects: Project[] = intersec(product.projects, material.projects, "slug")

  return (
    <Layout subMenu="products" pageTitle={product.name}>
      <Sheet>
        <ImageColumn>
          <Square>
            <Image fluid={material.background.fluid} />
            <Image fluid={format.image.fluid} />
          </Square>
        </ImageColumn>
        <Specs>
          <div>
            <FormattedMessage id="lining.selectMaterial" tagName="h4" />
            {materials.map((current) => {
              const { reference, image, name } = current
              return (
                <MaterialButton
                  key={reference}
                  $selected={reference === material.reference}
                  onClick={() => {
                    window.location.hash = reference
                  }}
                  title={name}
                >
                  <Image fixed={image.fixed} />
                </MaterialButton>
              )
            })}
            {categories.map((category) => (
              <>
                <h4>{category.name}</h4>
                {category.products.map(({ reference, image, name }) => {
                  return (
                    <LiningButton
                      key={reference}
                      $selected={reference === product.reference}
                      to={`/products/lining/${reference}#${material.reference}`}
                      title={name}
                    >
                      <Image fixed={image.fixed} />
                    </LiningButton>
                  )
                })}
              </>
            ))}

            {product.formats.length > 1 && (
              <>
                <FormattedMessage id="lining.selectFormat" tagName="h4" />
                {product.formats.map((current) => {
                  const { letter, name } = current
                  return (
                    <FormatButton
                      key={letter}
                      $selected={letter === format.letter}
                      onClick={() => {
                        setFormat(current)
                      }}
                      title={name}
                    >
                      {letter}
                    </FormatButton>
                  )
                })}
              </>
            )}
            <FormattedMessage id="lining.material" tagName="h4" />
            <Spec id="material.name" value={material.name} />
            <MoreInfos>
              <Link to={`/products/material/${material.reference}/`}>
                <FormattedMessage id="lining.infos" />
              </Link>
            </MoreInfos>
            <FormattedMessage id="lining.pattern" tagName="h4" />
            <Spec id="lining.name" value={product.name} />
            <Spec id="lining.reference" value={getReference(product.reference, format.letter.trim())} />
            {product.formats.length > 1 && <Spec id="lining.format" value={format.name} />}
            <Spec id="lining.density" value={String(format.density)} suffix="lining.pieces" />
            <Spec id="lining.size" value={format.size} />
          </div>
          <Bottom>
            <Link to="/catalog/">
              <FormattedMessage id="lining.download" />
              <br />
              <Download />
            </Link>
          </Bottom>
        </Specs>
      </Sheet>
      <Projects boxed list={projects} />
    </Layout>
  )
}

const getReference = (reference: string, format: string) => (format ? `${reference}.${format}` : reference)

export default LiningPage

export const query = graphql`
  query {
    websites: allContentfulSiteWeb {
      nodes {
        node_locale
        collections {
          contentful_id
          name
          materials {
            reference
            name
            image {
              fixed(width: 50) {
                ...GatsbyContentfulFixed
              }
            }
            background {
              fluid(maxWidth: 1600, quality: 75) {
                ...GatsbyContentfulFluid_noBase64
              }
            }
            projects {
              slug
              photo {
                fluid(maxWidth: 800, quality: 75) {
                  ...GatsbyContentfulFluid
                }
              }
            }
          }
        }
        categories {
          contentful_id
          name
          products {
            reference
            name
            image {
              fixed(width: 50) {
                ...GatsbyContentfulFixed_noBase64
              }
            }
            formats {
              letter
              name
              size
              density
              image {
                fluid(maxWidth: 1600, quality: 75) {
                  ...GatsbyContentfulFluid_noBase64
                }
              }
            }
            projects {
              slug
              photo {
                fluid(maxWidth: 800, quality: 75) {
                  ...GatsbyContentfulFluid
                }
              }
            }
          }
        }
      }
    }
  }
`
